import { useState } from "react";
import {
  AlignLeft,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  ContactRound,
  LayoutList,
  Plus,
  Trash2,
} from "lucide-react";

const inputClass =
  "mt-2 w-full rounded-lg border border-white/10 bg-[#11161a] px-3.5 py-3 text-sm text-white outline-none transition focus:border-brand/70 focus:ring-1 focus:ring-brand/50 placeholder:text-white/25";

const PANELS = [
  { id: "text", label: "Page text", icon: AlignLeft },
  { id: "contact", label: "Contact details", icon: ContactRound },
  { id: "faqs", label: "FAQs", icon: CircleHelp },
  { id: "lists", label: "Cards & lists", icon: LayoutList },
];

const TEXT_GROUPS = [
  {
    title: "Search & browser",
    description: "Text used in the browser tab and search previews.",
    fields: [
      ["metadata.title", "Browser title"],
      ["metadata.description", "Search description", "textarea"],
    ],
  },
  {
    title: "Hero banner",
    fields: [
      ["hero.kicker", "Small heading"],
      ["hero.titleLine1", "Title line 1"],
      ["hero.titleLine2", "Title line 2"],
      ["hero.titleAccent", "Green title line"],
      ["hero.description", "Description", "textarea"],
      ["hero.imageAlt", "Background image description"],
      ["hero.scrollLabel", "Scroll label"],
    ],
  },
  {
    title: "Products section",
    description: "Use {count} for the product count and {models} for model/models.",
    fields: [
      ["productsSection.kicker", "Small heading"],
      ["productsSection.title", "Heading"],
      ["productsSection.description", "Description", "textarea"],
    ],
  },
  {
    title: "Comparison section",
    fields: [
      ["comparison.kicker", "Small heading"],
      ["comparison.title", "Heading"],
      ["comparison.description", "Description", "textarea"],
      ["comparison.empty", "No selection message"],
      ["comparison.help", "Help prompt"],
      ["comparison.specification", "Specification column label"],
    ],
  },
  {
    title: "Technology section",
    fields: [
      ["technologySection.kicker", "Small heading"],
      ["technologySection.title", "Heading"],
      ["technologySection.description", "Description", "textarea"],
    ],
  },
  {
    title: "Why evLUXE section",
    fields: [
      ["whySection.kicker", "Small heading"],
      ["whySection.title", "Heading"],
      ["whySection.description", "Description", "textarea"],
    ],
  },
  {
    title: "About and legacy section",
    fields: [
      ["aboutSection.kicker", "Small heading"],
      ["aboutSection.title", "Heading"],
      ["aboutSection.description", "Description", "textarea"],
      ["aboutSection.visionPrefix", "Vision introduction"],
      ["aboutSection.vision1", "Vision phrase 1"],
      ["aboutSection.vision2", "Vision phrase 2"],
    ],
  },
  {
    title: "Dealer section",
    fields: [
      ["dealerSection.kicker", "Small heading"],
      ["dealerSection.title", "Heading"],
      ["dealerSection.description", "Description", "textarea"],
    ],
  },
  {
    title: "Interest form",
    fields: [
      ["formSection.kicker", "Small heading"],
      ["formSection.title", "Heading"],
      ["formSection.description", "Description", "textarea"],
      ["formSection.successTitle", "Success heading"],
      ["formSection.successDescription", "Success message"],
      ["formSection.consent", "Consent text", "textarea"],
      ["formSection.labels.fullName", "Full name label"],
      ["formSection.labels.phone", "Phone label"],
      ["formSection.labels.email", "Email label"],
      ["formSection.labels.city", "City label"],
      ["formSection.labels.model", "Preferred model label"],
      ["formSection.labels.buyerType", "Buyer type label"],
      ["formSection.labels.enquiryType", "Enquiry type label"],
      ["formSection.labels.message", "Message label"],
      ["formSection.placeholders.fullName", "Full name placeholder"],
      ["formSection.placeholders.phone", "Phone placeholder"],
      ["formSection.placeholders.email", "Email placeholder"],
      ["formSection.placeholders.city", "City placeholder"],
      ["formSection.placeholders.model", "Model placeholder"],
      ["formSection.placeholders.buyerType", "Buyer type placeholder"],
      ["formSection.placeholders.enquiryType", "Enquiry placeholder"],
      ["formSection.placeholders.message", "Message placeholder"],
      ["formSection.errors.name", "Missing name error"],
      ["formSection.errors.phoneRequired", "Missing phone error"],
      ["formSection.errors.phoneInvalid", "Invalid phone error"],
      ["formSection.errors.email", "Invalid email error"],
      ["formSection.errors.city", "Missing city error"],
      ["formSection.errors.consent", "Missing consent error"],
    ],
  },
  {
    title: "Contact section",
    fields: [
      ["contactSection.kicker", "Small heading"],
      ["contactSection.title", "Heading"],
      ["contactSection.description", "Description", "textarea"],
      ["contactSection.callTitle", "Phone card title"],
      ["contactSection.emailTitle", "Email card title"],
      ["contactSection.whatsappTitle", "WhatsApp card title"],
      ["contactSection.websiteTitle", "Website card title"],
    ],
  },
  {
    title: "FAQ heading",
    fields: [
      ["faqSection.kicker", "Small heading"],
      ["faqSection.title", "Heading"],
    ],
  },
  {
    title: "Footer",
    fields: [
      ["footer.description", "Company description", "textarea"],
      ["footer.quickLinksTitle", "Quick links heading"],
      ["footer.modelsTitle", "Models heading"],
      ["footer.contactTitle", "Contact heading"],
      ["footer.copyright", "Copyright text after the year"],
      ["footer.privacyLabel", "Privacy link label"],
      ["footer.privacyUrl", "Privacy link URL"],
      ["footer.termsLabel", "Terms link label"],
      ["footer.termsUrl", "Terms link URL"],
    ],
  },
  {
    title: "Buttons and common labels",
    fields: [
      ["actions.registerInterest", "Register interest"],
      ["actions.viewModels", "View models"],
      ["actions.compareModel", "Compare model"],
      ["actions.viewSpecs", "View specifications"],
      ["actions.enquireWhatsApp", "WhatsApp enquiry"],
      ["actions.availableColours", "Available colours"],
      ["actions.applyDealership", "Apply for dealership"],
      ["actions.talkToSales", "Talk to sales"],
      ["actions.viewMap", "View map"],
      ["actions.chatWhatsApp", "Chat on WhatsApp"],
      ["actions.continueWhatsApp", "Continue on WhatsApp"],
      ["actions.submitAnother", "Submit another enquiry"],
      ["actions.submitForm", "Submit form"],
      ["actions.submittingForm", "Submitting state"],
    ],
  },
  {
    title: "Product specification labels",
    fields: [
      ["productLabels.motor", "Motor"],
      ["productLabels.speedMeter", "Speed meter"],
      ["productLabels.voltage", "Voltage"],
      ["productLabels.wheelRim", "Wheel rim"],
      ["productLabels.batteryType", "Battery type"],
      ["productLabels.brakingSystem", "Braking system"],
      ["productLabels.suspension", "Suspension"],
      ["productLabels.batteryAmpere", "Battery ampere"],
      ["productLabels.tyreSize", "Tyre size"],
      ["productLabels.maxLoading", "Maximum loading"],
    ],
  },
];

const getAtPath = (object, path) =>
  path.split(".").reduce((current, key) => current?.[key], object);

const setAtPath = (object, path, value) => {
  const next = JSON.parse(JSON.stringify(object));
  const keys = path.split(".");
  let target = next;
  keys.slice(0, -1).forEach((key) => {
    target[key] = target[key] ?? {};
    target = target[key];
  });
  target[keys[keys.length - 1]] = value;
  return next;
};

const LabeledField = ({ label, value, onChange, type = "text", hint }) => (
  <label className="block text-sm text-white/75">
    <span className="font-medium">{label}</span>
    {hint && <span className="ml-2 text-xs font-normal text-white/35">{hint}</span>}
    {type === "textarea" ? (
      <textarea value={value ?? ""} onChange={(event) => onChange(event.target.value)} rows={4} className={inputClass} />
    ) : (
      <input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} className={inputClass} />
    )}
  </label>
);

const SectionCard = ({ title, description, children }) => (
  <section className="rounded-xl border border-white/10 bg-white/[0.025] p-5">
    <h2 className="font-display text-lg font-semibold">{title}</h2>
    {description && <p className="mt-1 text-xs leading-relaxed text-white/35">{description}</p>}
    <div className="mt-5 grid gap-5 sm:grid-cols-2">{children}</div>
  </section>
);

const ObjectListEditor = ({ title, description, items, fields, newItem, onChange }) => {
  const updateItem = (index, key, value) => {
    onChange(items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  };
  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <SectionCard title={title} description={description}>
      <div className="space-y-3 sm:col-span-2">
        {items.map((item, index) => (
          <div key={index} className="rounded-lg border border-white/10 bg-black/15 p-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-xs text-brand">ITEM {String(index + 1).padStart(2, "0")}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => move(index, -1)} disabled={index === 0} className="rounded p-1.5 text-white/45 hover:text-white disabled:opacity-20" title="Move up"><ChevronUp size={15} /></button>
                <button onClick={() => move(index, 1)} disabled={index === items.length - 1} className="rounded p-1.5 text-white/45 hover:text-white disabled:opacity-20" title="Move down"><ChevronDown size={15} /></button>
                <button onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))} className="ml-1 rounded p-1.5 text-red-300 hover:bg-red-500/10" title="Remove"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map(([key, label, type]) => (
                <div key={key} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <LabeledField label={label} type={type} value={item[key]} onChange={(value) => updateItem(index, key, value)} />
                </div>
              ))}
            </div>
          </div>
        ))}
        <button onClick={() => onChange([...items, { ...newItem }])} className="inline-flex items-center gap-2 rounded-lg border border-brand/30 px-4 py-2.5 text-sm font-semibold text-brand hover:bg-brand/10">
          <Plus size={15} /> Add item
        </button>
      </div>
    </SectionCard>
  );
};

const StringListEditor = ({ title, items, onChange }) => (
  <SectionCard title={title}>
    <div className="space-y-3 sm:col-span-2">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2">
          <input value={item} onChange={(event) => onChange(items.map((value, itemIndex) => itemIndex === index ? event.target.value : value))} className={`${inputClass} mt-0`} />
          <button onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))} className="rounded-lg border border-red-500/20 px-3 text-red-300 hover:bg-red-500/10" title="Remove"><Trash2 size={15} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...items, "New option"])} className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline"><Plus size={15} /> Add option</button>
    </div>
  </SectionCard>
);

export const SiteContentEditor = ({ content, onChange }) => {
  const [panel, setPanel] = useState("text");
  const update = (path, value) => onChange(setAtPath(content, path, value));

  return (
    <div className="space-y-5">
      <div className="flex gap-2 overflow-x-auto rounded-xl border border-white/10 bg-white/[0.025] p-2">
        {PANELS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setPanel(id)} className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2.5 text-sm transition ${panel === id ? "bg-brand text-black font-semibold" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {panel === "text" && (
        <>
          <SectionCard title="Navigation menu">
            {(content.navigation || []).map((item, index) => (
              <LabeledField key={item.id} label={`${item.id} label`} value={item.label} onChange={(value) => update("navigation", content.navigation.map((navItem, navIndex) => navIndex === index ? { ...navItem, label: value } : navItem))} />
            ))}
          </SectionCard>
          {TEXT_GROUPS.map((group) => (
            <SectionCard key={group.title} title={group.title} description={group.description}>
              {group.fields.map(([path, label, type]) => (
                <div key={path} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <LabeledField label={label} type={type} value={getAtPath(content, path)} onChange={(value) => update(path, value)} />
                </div>
              ))}
            </SectionCard>
          ))}
        </>
      )}

      {panel === "contact" && (
        <>
          <SectionCard title="Primary contact details" description="These values update the contact section, footer, call buttons and WhatsApp links.">
            <LabeledField label="Email address" type="email" value={content.contact.email} onChange={(value) => update("contact.email", value)} />
            <LabeledField label="WhatsApp number" hint="country code + number, digits only" value={content.contact.whatsappNumber} onChange={(value) => update("contact.whatsappNumber", value.replace(/[^0-9]/g, ""))} />
            <LabeledField label="Website label" value={content.contact.website} onChange={(value) => update("contact.website", value)} />
            <LabeledField label="Website URL" type="url" value={content.contact.websiteUrl} onChange={(value) => update("contact.websiteUrl", value)} />
          </SectionCard>
          <ObjectListEditor title="Phone numbers" items={content.contact.phones || []} fields={[["display", "Displayed number"], ["tel", "Tap-to-call number"]]} newItem={{ display: "", tel: "+91" }} onChange={(items) => update("contact.phones", items)} />
          <SectionCard title="Corporate office">
            <LabeledField label="Location label" value={content.contact.corporateOffice.label} onChange={(value) => update("contact.corporateOffice.label", value)} />
            <LabeledField label="Google Maps URL" type="url" value={content.contact.corporateOffice.maps} onChange={(value) => update("contact.corporateOffice.maps", value)} />
            <div className="sm:col-span-2"><LabeledField label="Address" type="textarea" value={content.contact.corporateOffice.address} onChange={(value) => update("contact.corporateOffice.address", value)} /></div>
          </SectionCard>
          <SectionCard title="Manufacturing unit">
            <LabeledField label="Location label" value={content.contact.manufacturingUnit.label} onChange={(value) => update("contact.manufacturingUnit.label", value)} />
            <LabeledField label="Google Maps URL" type="url" value={content.contact.manufacturingUnit.maps} onChange={(value) => update("contact.manufacturingUnit.maps", value)} />
            <div className="sm:col-span-2"><LabeledField label="Address" type="textarea" value={content.contact.manufacturingUnit.address} onChange={(value) => update("contact.manufacturingUnit.address", value)} /></div>
          </SectionCard>
          <ObjectListEditor title="Social links" items={content.footer.socialLinks || []} fields={[["platform", "Platform"], ["url", "Profile URL"]]} newItem={{ platform: "Social", url: "#" }} onChange={(items) => update("footer.socialLinks", items)} />
        </>
      )}

      {panel === "faqs" && (
        <ObjectListEditor title="Frequently asked questions" description="Questions appear in this order on the website." items={content.faqSection.items || []} fields={[["q", "Question"], ["a", "Answer", "textarea"]]} newItem={{ q: "New question", a: "Add the answer here." }} onChange={(items) => update("faqSection.items", items)} />
      )}

      {panel === "lists" && (
        <>
          <ObjectListEditor title="Hero highlights" items={content.hero.proofPoints || []} fields={[["label", "Text"]]} newItem={{ icon: "ShieldCheck", label: "New highlight" }} onChange={(items) => update("hero.proofPoints", items)} />
          <ObjectListEditor title="Hero statistics" items={content.hero.stats || []} fields={[["value", "Value"], ["suffix", "Suffix"], ["label", "Label"], ["display", "Optional display text"]]} newItem={{ value: "0", suffix: "", label: "New statistic", display: "" }} onChange={(items) => update("hero.stats", items)} />
          <ObjectListEditor title="Technology cards" items={content.technologySection.items || []} fields={[["title", "Title"], ["desc", "Description", "textarea"]]} newItem={{ title: "New technology", desc: "Description", icon: "Cog" }} onChange={(items) => update("technologySection.items", items)} />
          <ObjectListEditor title="Why evLUXE cards" items={content.whySection.items || []} fields={[["short", "Card title"], ["desc", "Description", "textarea"]]} newItem={{ title: "New benefit", short: "New benefit", desc: "Description", icon: "Sparkles" }} onChange={(items) => update("whySection.items", items)} />
          <ObjectListEditor title="Company timeline" items={content.aboutSection.timeline || []} fields={[["year", "Year"], ["text", "Description", "textarea"]]} newItem={{ year: new Date().getFullYear().toString(), text: "New milestone" }} onChange={(items) => update("aboutSection.timeline", items)} />
          <ObjectListEditor title="About highlights" items={content.aboutSection.highlights || []} fields={[["value", "Value"], ["label", "Label"], ["desc", "Description", "textarea"]]} newItem={{ value: "New", label: "Highlight", desc: "Description" }} onChange={(items) => update("aboutSection.highlights", items)} />
          <ObjectListEditor title="Dealer statistics" items={content.dealerSection.stats || []} fields={[["value", "Value"], ["suffix", "Suffix"], ["display", "Optional display text"], ["label", "Label", "textarea"]]} newItem={{ value: "0", suffix: "+", label: "New dealer statistic", display: "" }} onChange={(items) => update("dealerSection.stats", items)} />
          <StringListEditor title="Buyer type options" items={content.formSection.buyerTypes || []} onChange={(items) => update("formSection.buyerTypes", items)} />
          <StringListEditor title="Enquiry type options" items={content.formSection.enquiryTypes || []} onChange={(items) => update("formSection.enquiryTypes", items)} />
        </>
      )}
    </div>
  );
};

export default SiteContentEditor;
